import { Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { BrandKitColorSystem } from "../models/BrandKit";
import { Button } from "./ui/button";

/** Approximate luminance for display ordering — light dots lead. */
function luminance(hex: string): number {
  const n = parseInt(hex.replace("#", ""), 16);
  return (
    0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)
  );
}

/** A pairing is a SET of colors; render it sorted so identical sets look identical. */
function comboColors(combo: BrandKitColorSystem["combinations"][number]): string[] {
  return [
    ...new Set(
      [combo.canvas, combo.text, ...combo.accents].map((c) => c.toUpperCase())
    ),
  ].sort((a, b) => luminance(b) - luminance(a));
}

/**
 * Read-only view of the kit's AI-derived color pairings. Purely a renderer —
 * generation reads the same object from the kit doc, so what's shown here is
 * exactly what posts rotate through.
 */
export function BrandColorSystemCard({
  system,
  paletteColors = [],
  isPairing = false,
  canPairNow = false,
  onPairNow,
}: {
  system: BrandKitColorSystem | undefined | null;
  /** Current palette in the editor — used to detect stale pairings. */
  paletteColors?: string[];
  isPairing?: boolean;
  /** False for kits that haven't been saved yet (no id to derive against). */
  canPairNow?: boolean;
  onPairNow?: () => void;
}) {
  if (isPairing) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Pairing your colors…
      </div>
    );
  }

  const pairNowButton =
    canPairNow && onPairNow ? (
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={onPairNow}
      >
        Preview
      </Button>
    ) : null;

  if (!system || system.combinations.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">
          {canPairNow
            ? "AI pairs your colors when you save — each post uses one pairing."
            : "AI pairs your colors when you first save — each post uses one pairing."}
        </p>
        {pairNowButton}
      </div>
    );
  }

  // Pairings only apply to palettes of 4+ colors — below that, posts use
  // the colors directly and any displayed system is a leftover (the server
  // cleans it from the kit doc on next save).
  const inapplicable = paletteColors.length <= 3;

  // Stale when the palette no longer matches what the pairings were
  // derived from.
  const systemColors = new Set(
    Object.keys(system.roles).map((c) => c.toUpperCase())
  );
  const currentColors = paletteColors.map((c) => c.toUpperCase());
  const paletteMatches =
    currentColors.length === systemColors.size &&
    currentColors.every((c) => systemColors.has(c));
  const stale = !paletteMatches;

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "flex flex-wrap gap-x-4 gap-y-2",
          (stale || inapplicable) && "opacity-40"
        )}
      >
        {system.combinations.map((combo, i) => {
          const colors = comboColors(combo);
          return (
            <div
              key={`${colors.join("-")}-${i}`}
              className="flex items-center -space-x-1.5"
              title={colors.join(", ")}
            >
              {colors.map((color, j) => (
                <span
                  key={color}
                  className="h-6 w-6 rounded-full border border-black/10 ring-2 ring-background"
                  style={{ backgroundColor: color, zIndex: colors.length - j }}
                />
              ))}
            </div>
          );
        })}
      </div>
      {inapplicable ? (
        <p className="text-xs text-muted-foreground">
          With 3 or fewer colors, posts use them directly — pairings no
          longer apply.
        </p>
      ) : stale ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-muted-foreground">
            Palette changed — new pairings will be created when you save.
          </p>
          {pairNowButton}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {system.source === "heuristic" &&
            "Derived with quick rules — AI retries on next save. "}
          Each post uses one of these pairings.
        </p>
      )}
    </div>
  );
}
